/**
 * Mentions légales (mandatory under French Law / LCEN article 6-III).
 *
 * Kept deliberately autonomous from shariti.com so PREREC is legally
 * self-contained as an internal recruitment platform.
 */
import { ArrowLeft } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";

import PublicFooter from "../components/layout/PublicFooter";
import { goBack } from "../lib/navigation";


export default function MentionsLegales() {
    const navigate = useNavigate();
    return (
        <div className="min-h-screen flex flex-col bg-shariti-cream">
            <main className="flex-1 max-w-3xl mx-auto px-6 py-10 md:py-14 w-full">
                <button
                    onClick={() => goBack(navigate, "/login")}
                    data-testid="back-to-login"
                    className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-slate-500 hover:text-shariti-orange transition"
                >
                    <ArrowLeft className="h-3.5 w-3.5" />
                    Retour
                </button>

                <h1 className="mt-6 text-3xl md:text-4xl font-black tracking-tight text-shariti-black">
                    Mentions légales
                </h1>
                <p className="mt-2 text-xs font-bold uppercase tracking-widest text-slate-400">
                    Dernière mise à jour : {new Date().toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}
                </p>

                <div className="mt-8 space-y-7 text-sm leading-relaxed text-slate-700">
                    <Section title="1. Éditeur du site">
                        <p>
                            La plateforme <strong>PREREC</strong> est éditée et exploitée par <strong>SHARITI</strong>,
                            dans le cadre de ses activités de recrutement et de gestion des candidatures.
                        </p>
                        <ul className="list-none mt-2 space-y-1">
                            <li><strong>Dénomination :</strong> SHARITI</li>
                            <li><strong>Site principal :</strong> <a className="text-shariti-orange hover:underline" href="https://shariti.com" target="_blank" rel="noreferrer noopener">https://shariti.com</a></li>
                            <li><strong>Plateforme PREREC :</strong> <a className="text-shariti-orange hover:underline" href="https://prerec.shariti.com" target="_blank" rel="noreferrer noopener">https://prerec.shariti.com</a></li>
                            <li><strong>Contact :</strong> <a className="text-shariti-orange hover:underline" href="mailto:smile@shariti.com">smile@shariti.com</a></li>
                        </ul>
                    </Section>

                    <Section title="2. Objet de la plateforme">
                        <p>
                            PREREC est une <strong>plateforme interne de prérecrutement et de gestion des candidatures</strong> utilisée
                            exclusivement par Shariti dans le cadre de ses activités de recrutement. Elle agrège les candidatures
                            reçues via les <strong>différentes sources d'acquisition</strong> utilisées par Shariti (site internet,
                            outils de gestion de recrutement, sites d'annonces partenaires, formulaires en ligne) et permet à
                            l'équipe interne de qualifier, suivre et organiser les entretiens des candidats.
                        </p>
                    </Section>

                    <Section title="3. Hébergement">
                        <p>
                            La plateforme PREREC est hébergée sur une infrastructure cloud sécurisée. Les bases
                            de données candidats sont stockées en Union européenne et bénéficient d'un chiffrement
                            au repos et en transit (TLS 1.3).
                        </p>
                    </Section>

                    <Section title="4. Propriété intellectuelle">
                        <p>
                            L'ensemble des éléments composant la plateforme PREREC (interface, charte graphique, logos,
                            textes, code source) est la propriété exclusive de Shariti, sauf mention contraire.
                            Toute reproduction, représentation, modification ou exploitation, totale ou partielle, sans
                            autorisation écrite préalable est interdite.
                        </p>
                    </Section>

                    <Section title="5. Accès à la plateforme">
                        <p>
                            L'accès à PREREC est strictement réservé aux collaborateurs de Shariti habilités (administrateurs,
                            pré-recruteurs, référents terrain). Chaque utilisateur dispose d'identifiants personnels
                            qu'il s'engage à conserver de manière confidentielle.
                        </p>
                    </Section>

                    <Section title="6. Données personnelles">
                        <p>
                            Les modalités de collecte, de traitement et de conservation des données personnelles sont
                            décrites en détail dans notre{" "}
                            <Link to="/confidentialite" className="text-shariti-orange hover:underline font-bold">
                                Politique de confidentialité
                            </Link>.
                        </p>
                    </Section>

                    <Section title="7. Contact">
                        <p>
                            Pour toute question relative à la plateforme PREREC, à la protection des données personnelles
                            ou pour exercer vos droits, vous pouvez nous contacter à l'adresse :{" "}
                            <a className="text-shariti-orange hover:underline font-bold" href="mailto:smile@shariti.com">
                                smile@shariti.com
                            </a>.
                        </p>
                    </Section>
                </div>
            </main>
            <PublicFooter />
        </div>
    );
}


function Section({ title, children }) {
    return (
        <section>
            <h2 className="text-lg font-extrabold text-shariti-black mb-2">{title}</h2>
            <div className="space-y-2">{children}</div>
        </section>
    );
}
